import React from 'react';
import PropTypes from 'prop-types';

import {connect, dispatch} from 'utils/state_manager';

import {Box, Button, Stack} from 'views/components';
import {ArrowForwardIos} from 'views/icons';
import {WasHelpfulCard, AiQuestionCard, Translate, translate} from 'views/module';

import backend from 'utils/backend';

import moment from 'moment';

import {primaryButtonSx} from '../../utils/button_styles';

import ExpectedCostCard from './cards/ExpectedCostCard';
import PlansCard from './cards/PlansCard';
import ScheduleCard from './cards/ScheduleCard';
import TitleCard from './cards/TitleCard';

const ScheduleStep = (props) => {
  const {onNext, clientRn, statusProgressWrap: progressWrap} = props;

  const [events, setEvents] = React.useState([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const scheduleCardRef = React.useRef(null);

  const getDurationHours = React.useCallback((startTime, endTime) => {
    const startMoment = moment(startTime);
    const endMoment = moment(endTime);

    if (!startMoment.isValid() || !endMoment.isValid()) {
      return 0;
    }

    const adjustedEndMoment = endMoment.isAfter(startMoment) ? endMoment : endMoment.clone().add(1, 'day');
    return adjustedEndMoment.diff(startMoment, 'minutes') / 60;
  }, []);

  const parseHours = React.useCallback((rawValue) => {
    const durationText = `${rawValue ?? ''}`.trim();
    if (!durationText) {
      return 0;
    }

    const parsedHours = Number.parseFloat(durationText.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsedHours) ? parsedHours : 0;
  }, []);

  const hoursPerWeek = React.useMemo(() => {
    const totalHours = events.reduce((sum, eventItem) => {
      if (eventItem.scheduleType === 'FLEXIBLE') {
        const preferredDayCount = eventItem.preferredDays?.length ?? 0;
        return sum + (parseHours(eventItem.visitDuration) * preferredDayCount);
      }

      if (eventItem.scheduleType === 'ON_CALL') {
        return sum;
      }

      const repeatCount = eventItem.repeats?.length ?? 0;
      return sum + (getDurationHours(eventItem.startTime, eventItem.endTime) * repeatCount);
    }, 0);

    return Math.round(totalHours * 10) / 10;
  }, [events, getDurationHours, parseHours]);

  const weeklyAmount = React.useMemo(() => {
    const rate = 35;
    return Math.round(hoursPerWeek * rate);
  }, [hoursPerWeek]);

  const formDefinition = React.useMemo(() => ({
    formVersion: 2,
    title: translate('schedulestep.ScheduleStep.FormDefinitionTitle'),
    description: translate('schedulestep.ScheduleStep.FormDefinitionDescription'),
    questions: {
      'Schedule-events': {
        questionId: 'Schedule-events',
        fieldType: 'schedule',
        rank: 1,
        questionData: {
          label: translate('schedulestep.ScheduleStep.FormQuestionLabel'),
          helpText: '',
          required: true,
        },
      },
    },
  }), []);

  const buildScheduleAnswersByType = React.useCallback((scheduleEvents, scheduleType) => {
    const eventsByType = scheduleEvents.filter((eventItem) => eventItem.scheduleType === scheduleType);
    const normalizedEvents = eventsByType.map((eventItem) => {
      const normalizedEvent = {...eventItem};
      delete normalizedEvent.scheduleType;
      return normalizedEvent;
    });

    return [
      {
        questionId: 'Schedule-events',
        fieldType: 'schedule',
        value: {
          events: normalizedEvents,
        },
      },
    ];
  }, []);

  const submitScheduleForms = React.useCallback(async (scheduleEvents) => {
    const recurringAnswers = buildScheduleAnswersByType(scheduleEvents, 'RECURRING');
    const recurringEvents = recurringAnswers[0].value.events;
    if (recurringEvents.length > 0) {
      await backend.client.intakeForm.startForm(clientRn, 'CLIENT_SCHEDULE_INTAKE_FORM', {
        formDefinition,
        answers: recurringAnswers,
        generateSummary: false,
      });
    }

    const flexibleAnswers = buildScheduleAnswersByType(scheduleEvents, 'FLEXIBLE');
    const flexibleEvents = flexibleAnswers[0].value.events;
    if (flexibleEvents.length > 0) {
      await backend.client.intakeForm.startForm(clientRn, 'CLIENT_SCHEDULE_FLEXIBLE_INTAKE_FORM', {
        formDefinition,
        answers: flexibleAnswers,
        generateSummary: false,
      });
    }

    const onCallAnswers = buildScheduleAnswersByType(scheduleEvents, 'ON_CALL');
    const onCallEvents = onCallAnswers[0].value.events;
    if (onCallEvents.length > 0) {
      await backend.client.intakeForm.startForm(clientRn, 'CLIENT_SCHEDULE_ONCALL_INTAKE_FORM', {
        formDefinition,
        answers: onCallAnswers,
        generateSummary: false,
      });
    }
  }, [buildScheduleAnswersByType, clientRn, formDefinition]);

  const persistScheduleEvents = React.useCallback((scheduleEvents) => {
    progressWrap(async () => {
      try {
        await submitScheduleForms(scheduleEvents);
      } catch (error) {
        console.error('Failed to autosave schedule form:', error);
      }
    });
  }, [progressWrap, submitScheduleForms]);

  const handleEventsChange = React.useCallback((nextEvents) => {
    setEvents(nextEvents);
    persistScheduleEvents(nextEvents);
  }, [persistScheduleEvents]);

  const canSubmit = !isSubmitting;

  const handleSubmit = React.useCallback(() => {
    if (!canSubmit) {
      return;
    }

    if (events.length === 0) {
      if (scheduleCardRef.current) {
        scheduleCardRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
      return;
    }

    progressWrap(async () => {
      setIsSubmitting(true);
      try {
        await submitScheduleForms(events);
        await backend.client.updateStatus(clientRn, {status: 'DETAILS_NEEDED'});
        onNext();
      } catch (error) {
        console.error('Failed to submit schedule form:', error);
      } finally {
        setIsSubmitting(false);
      }
    });
  }, [canSubmit, events, onNext, progressWrap, submitScheduleForms]);

  return (
    <Stack sx={{gap: '8px'}}>
      <TitleCard />
      <Box ref={scheduleCardRef} sx={{scrollMarginTop: '24px'}}>
        <ScheduleCard events={events} onEventsChange={handleEventsChange} />
      </Box>
      <PlansCard />
      <ExpectedCostCard weeklyAmount={weeklyAmount} hoursPerWeek={hoursPerWeek} />
      <WasHelpfulCard articleTitle="Intake Form | Schedule | Feedback (Thumbs)" articleRN="RN:ARTICLE-V1-ScheduleHelpful" />
      <AiQuestionCard clientRn={clientRn} page='Intake Form | Schedule' />

      <Box sx={{textAlign: 'right', mt: '32px'}}>
        <Button onClick={handleSubmit} sx={primaryButtonSx} endIcon={<ArrowForwardIos />} disabled={!canSubmit}>
          <Translate expression={'schedulestep.ScheduleStep.CreateCarePlan'} />
        </Button>
      </Box>
    </Stack>
  );
};

ScheduleStep.propTypes = {
  onNext: PropTypes.func,
  clientRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  clientRn: state.user.clientRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(ScheduleStep));
